import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, ButtonGroup, Chip, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { TaskFlowDto } from '@shared/services/apiService/apiService';
import CustomSwitch from '@shared/components/forms/theme-elements/CustomSwitch';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { toast } from 'react-toastify';
import { useTaskFlowEditorState } from '@features/Admin/Agent/taskFlow/state/useTaskFlowEditorState';
import {
  TASK_TYPES,
  TASK_TYPE_REGISTRY,
} from '@features/Admin/Agent/taskFlow/domain/taskTypeRegistry';
import { hasTaskFlowErrors } from '@features/Admin/Agent/taskFlow/domain/validation';
import { TaskFlowCanvas } from './TaskFlowCanvas';
import { TaskFlowDrawer } from './TaskFlowDrawer';

export type TaskFlowSaveState = {
  canSave: boolean;
  isSaving: boolean;
};

export type TaskFlowPanelHandle = {
  save: () => Promise<void>;
  canSave: boolean;
  isSaving: boolean;
};

type TaskFlowPanelProps = {
  initialTaskFlow: TaskFlowDto | null | undefined;
  readOnly: boolean;
  profileName?: string;
  onSave: (taskFlow: TaskFlowDto | null) => Promise<TaskFlowDto | null>;
  onSaveStateChange?: (state: TaskFlowSaveState) => void;
};

export const TaskFlowPanel = React.forwardRef<TaskFlowPanelHandle, TaskFlowPanelProps>(
  function TaskFlowPanel(
    { initialTaskFlow, readOnly, profileName, onSave, onSaveStateChange },
    ref,
  ) {
    const { t } = useTranslation();
    const state = useTaskFlowEditorState(initialTaskFlow);
    const [isSaving, setIsSaving] = React.useState(false);
    const disabled = readOnly || isSaving;
    const canSave = !disabled && !state.hasErrors && state.isDirty;

    const handleSave = React.useCallback(async () => {
      if (state.hasErrors || !state.isDirty) {
        state.setSaveState('error');
        return;
      }
      try {
        setIsSaving(true);
        state.setSaveState('saving');
        const savedTaskFlow = await onSave(state.serializeForSave());
        state.markSaved(savedTaskFlow);
        toast.success(CustomSuccessToast, {
          closeButton: false,
          autoClose: 3500,
          icon: false,
          data: {
            title: t('common.success'),
            description: t('assistantsPage.modals.taskFlow.saved'),
          },
        });
      } catch {
        state.markSaveError({
          groupErrors: { taskFlow: t('assistantsPage.modals.taskFlow.invalid') },
          taskErrors: {},
        });
      } finally {
        setIsSaving(false);
      }
    }, [onSave, state, t]);

    React.useImperativeHandle(ref, () => ({ save: handleSave, canSave, isSaving }), [
      canSave,
      handleSave,
      isSaving,
    ]);

    React.useEffect(() => {
      onSaveStateChange?.({ canSave, isSaving });
    }, [canSave, isSaving, onSaveStateChange]);

    if (!state.group) {
      return (
        <Stack spacing={1.5} alignItems="flex-start" sx={{ p: { xs: 1, sm: 2 } }}>
          <Typography variant="body2" color="text.secondary">
            {t('assistantsPage.modals.taskFlow.emptyDescription')}
          </Typography>
          <Button
            type="button"
            variant="contained"
            startIcon={<AddIcon />}
            disabled={readOnly}
            onClick={state.startNewGroup}>
            {t('assistantsPage.modals.taskFlow.create')}
          </Button>
        </Stack>
      );
    }

    return (
      <Stack spacing={1.5} sx={{ height: '100%', minHeight: 0 }}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', lg: 'center' }}
          flexWrap="wrap"
          gap={1}>
          <ButtonGroup
            size="small"
            disabled={disabled}
            variant="outlined"
            sx={{ flexWrap: 'wrap' }}>
            {TASK_TYPES.map((type) => (
              <Button
                key={type}
                type="button"
                onClick={() => state.addTask(type, t(TASK_TYPE_REGISTRY[type].descriptionKey))}>
                + {t(`assistantsPage.modals.taskFlow.${TASK_TYPE_REGISTRY[type].shortLabelKey}`)}
              </Button>
            ))}
          </ButtonGroup>
          {state.isDirty && (
            <Chip
              size="small"
              label={t('assistantsPage.modals.taskFlow.unsaved')}
              color="warning"
              variant="outlined"
            />
          )}
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 0.5, sm: 2 }}
          useFlexGap
          flexWrap="wrap"
          sx={{ px: 0.5 }}>
          <FormSwitch
            label={t('assistantsPage.modals.taskFlow.autoStart')}
            checked={Boolean(state.group.autoStart)}
            disabled={disabled}
            onChange={(value) => state.updateGroupField('autoStart', value)}
          />
          <FormSwitch
            label={t('assistantsPage.modals.taskFlow.summarizeChatCtx')}
            checked={Boolean(state.group.summarizeChatCtx)}
            disabled={disabled}
            onChange={(value) => state.updateGroupField('summarizeChatCtx', value)}
          />
          <FormSwitch
            label={t('assistantsPage.modals.taskFlow.returnExceptions')}
            checked={Boolean(state.group.returnExceptions)}
            disabled={disabled}
            onChange={(value) => state.updateGroupField('returnExceptions', value)}
          />
        </Stack>

        {hasTaskFlowErrors(state.errors) && (
          <Alert severity="error" variant="outlined">
            {Object.values(state.errors.groupErrors)[0] ??
              t('assistantsPage.modals.taskFlow.validationSummary')}
          </Alert>
        )}

        <TaskFlowCanvas
          tasks={state.group.tasks}
          errors={state.errors}
          selectedTaskKey={state.selectedTaskKey}
          onSelectTask={state.selectTask}
          onReorderTask={state.reorderTask}
          disabled={disabled}
          height={{ xs: 340, sm: 380 }}
        />

        <TaskFlowDrawer
          open={Boolean(state.selectedTask)}
          task={state.selectedTask}
          errors={
            state.selectedTask ? (state.errors.taskErrors[state.selectedTask.clientKey] ?? {}) : {}
          }
          disabled={disabled}
          onClose={() => state.selectTask(null)}
          onUpdateTask={state.updateTask}
          onDeleteTask={state.removeTask}
        />

        <Box>
          <Typography variant="caption" color="text.secondary">
            {t('assistantsPage.modals.taskFlow.caption')}
            {profileName ? ` ${t('assistantsPage.modals.taskFlow.editing', { profileName })}` : ''}
          </Typography>
        </Box>
      </Stack>
    );
  },
);

const FormSwitch: React.FC<{
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (value: boolean) => void;
}> = ({ label, checked, disabled, onChange }) => (
  <Stack direction="row" spacing={0.75} alignItems="center">
    <CustomSwitch
      checked={checked}
      disabled={disabled}
      inputProps={{ 'aria-label': label }}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.checked)}
    />
    <Typography variant="body2">{label}</Typography>
  </Stack>
);
